import { FC } from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import Table from '@/components/RTable';
import Tooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResult } from '@/types/simulations';
import { formatNumberWithCommas } from '@/utils';

import { StyledTable } from './styles';

interface EnergyTableDataTypes {
  key: string;
  title: string | JSX.Element;
  value: string;
  earnedAmount?: string;
}

interface EnergyTableProps {
  simulationData: SimulationResult;
}

const EnergyTable: FC<EnergyTableProps> = ({ simulationData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();

  const energyDataTable: EnergyTableDataTypes[] = [
    {
      key: '1',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.energySales.salesRevenueWithStorage.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.energySales.salesRevenueWithStorage.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.energySales.salesRevenueWithStorage.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      value:
        formatNumberWithCommas(
          Number(simulationData.result.totalRevenueFromSale.toFixed(0))
        ) + ` ${currencySymbol}`,
      earnedAmount:
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitOfStorageFromSale.toFixed(0))
        ) + ` ${currencySymbol}`,
    },
    {
      key: '2',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.energySales.costOfPurchasedEnergyForSale.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.energySales.costOfPurchasedEnergyForSale.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.energySales.costOfPurchasedEnergyForSale.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      value:
        formatNumberWithCommas(
          Number(simulationData.result.costOfPurchasedEnergyStorage.toFixed(0))
        ) + ` ${currencySymbol}`,
    },
    {
      key: '3',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithStorage.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithStorage.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithStorage.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      value:
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitFromSale?.toFixed(0) || 0)
        ) + ` ${currencySymbol}`,
    },
    {
      key: '4',
      title: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '10px 24px',
            backgroundColor: theme.colors.blueLightest,
            borderTop: '3px solid white',
          }}
        >
          <Typography.Text className="fs-14-bold">
            {t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithoutStorage.title'
            )}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithoutStorage.title'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.energySales.grossProfitFromSaleWithoutStorage.description'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      value:
        formatNumberWithCommas(
          Number(simulationData.result.grossProfitFromSaleWos.toFixed(0))
        ) + ` ${currencySymbol}`,
    },
  ];

  const energyColumns = [
    {
      dataIndex: 'title',
      width: '28%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.value')}
          </Typography.Text>
        </Flex>
      ),
      dataIndex: 'value',
      key: 'value',
      width: '36%',
    },
    {
      title: (
        <Flex justify="center" align="center" gap={8}>
          <Typography.Text className="fs-14-bold">
            {t('result.finance.incomeBreakdown.tables.columns.earnedAmount')}
          </Typography.Text>
          <Tooltip
            title={t(
              'result.finance.incomeBreakdown.tables.columns.earnedAmount'
            )}
            description={t(
              'result.finance.incomeBreakdown.tables.columns.earnedAmountDescription'
            )}
          >
            <IconInfo fill="black" />
          </Tooltip>
        </Flex>
      ),
      dataIndex: 'earnedAmount',
      key: 'earnedAmount',
      width: '36%',
      onCell: (_record: EnergyTableDataTypes, rowIndex?: number) => {
        if (rowIndex === 0) {
          return {
            rowSpan: energyDataTable.length,
            style: {
              verticalAlign: 'middle',
              fontSize: '20px',
            },
          };
        } else {
          return { rowSpan: 0 };
        }
      },
      render: (text: string) => ({
        children: <span>{text}</span>,
      }),
    },
  ];
  return (
    <StyledTable>
      <Table
        dataSource={energyDataTable}
        columns={energyColumns}
        pagination={false}
      />
    </StyledTable>
  );
};

export default EnergyTable;
